import { AlertTriangle, Archive, ChevronDown, Mail, Send, Star } from 'lucide-react';
import { useState } from 'react';
import { EmailAttachmentList } from '@/components/email/EmailAttachmentList';
import { Spinner } from '@/components/ui';
import { Button, ConfirmDialog, ModalContentWrapper } from '@/components/ui';
import {
  useArchiveThread,
  useEmailsByLoan,
  useMarkEmailRead,
  useSendEmail,
  useStarEmail,
} from '@/hooks/use-email';
import { cn } from '@/lib/cn';
import { handleMutationError } from '@/lib/error-handling';
import { fmtCurrency, fmtDateOnly, fmtTimeAgo } from '@/lib/format';
import { resolveActiveTenantId, resolveAvailableTenants } from '@/lib/tenant-context';
import { useAuthStore } from '@/stores/auth-store';
import { LOAN_STATUS_CONFIG } from '@/types';
import type {
  EmailMessageStatus,
  EmailThread,
  Loan,
  LoanStatus,
  RepaymentScheduleItem,
} from '@/types';

const REGISTER_REPLY_MAILBOX_PATTERN =
  /@(?:[^@\s]*\.)?fwd(?:-[^@\s]+)?\.vestracorporate\.com\.au$/i;

function isRegisterReplyMailboxAddress(email?: string | null) {
  return Boolean(email && REGISTER_REPLY_MAILBOX_PATTERN.test(email.trim()));
}

function trimQuotedReplyContent(value?: string | null) {
  if (!value) {
    return '';
  }

  let cleaned = value.replace(/\r/g, '').trim();
  const lines = cleaned.split('\n');
  const quotedReplyStartIndex = lines.findIndex((line, index) => {
    const trimmedLine = line.trim();
    const nextLine = lines[index + 1]?.trim() || '';

    if (/^On .+wrote:$/i.test(trimmedLine)) {
      return true;
    }

    if (/^On .+$/i.test(trimmedLine) && nextLine === 'wrote:') {
      return true;
    }

    if (/^From:/i.test(trimmedLine)) {
      return true;
    }

    if (/^_{2,}$/.test(trimmedLine)) {
      return true;
    }

    return false;
  });

  if (quotedReplyStartIndex >= 0) {
    cleaned = lines.slice(0, quotedReplyStartIndex).join('\n').trim();
  }

  cleaned = cleaned
    .split('\n')
    .filter(line => !line.trim().startsWith('>'))
    .join('\n')
    .trim();

  return cleaned || value.trim();
}

function getDisplayParticipants(
  participants: Array<{ email: string; name?: string; role?: string }>,
) {
  const visibleParticipants = participants.filter(
    participant => !isRegisterReplyMailboxAddress(participant.email),
  );
  return visibleParticipants.length > 0 ? visibleParticipants : participants;
}

function ThreadCategoryBadge({ category }: { category?: string }) {
  if (category !== 'OFFER_LETTER') return null;

  return (
    <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-amber-700">
      Offer Letter
    </span>
  );
}

interface EmailTabProps {
  loanId: string;
  loan: Loan;
  borrowerEmail?: string;
  borrowerName?: string;
}

const EMAIL_STATUS_CONFIG: Record<EmailMessageStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: '#6b7280' },
  QUEUED: { label: 'Queued', color: '#f59e0b' },
  SENT: { label: 'Sent', color: '#3b82f6' },
  DELIVERED: { label: 'Delivered', color: '#10b981' },
  FAILED: { label: 'Failed', color: '#ef4444' },
  BOUNCED: { label: 'Bounced', color: '#dc2626' },
};

type EmailTemplateId = 'loan_approval' | 'payment_reminder' | 'maturity_warning' | 'arrears_notice';

type EmailTemplateRule = {
  id: EmailTemplateId;
  label: string;
  matchingStatuses: LoanStatus[];
};

const EMAIL_TEMPLATE_RULES: EmailTemplateRule[] = [
  { id: 'loan_approval', label: 'Approval Notice', matchingStatuses: ['APPROVED'] },
  {
    id: 'payment_reminder',
    label: 'Payment Reminder',
    matchingStatuses: ['DISBURSED', 'CURRENT'],
  },
  {
    id: 'maturity_warning',
    label: 'Maturity Warning',
    matchingStatuses: ['CURRENT', 'DELINQUENT_30', 'DELINQUENT_60', 'DELINQUENT_90'],
  },
  {
    id: 'arrears_notice',
    label: 'Arrears Notice',
    matchingStatuses: ['DELINQUENT_30', 'DELINQUENT_60', 'DELINQUENT_90', 'DEFAULT'],
  },
];

const EMAIL_TEMPLATE_RULES_BY_ID = EMAIL_TEMPLATE_RULES.reduce(
  (acc, rule) => {
    acc[rule.id] = rule;
    return acc;
  },
  {} as Record<EmailTemplateId, EmailTemplateRule>,
);

function getDaysOverdue(dueDate?: string): number {
  if (!dueDate) return 0;

  const due = new Date(dueDate);
  const today = new Date();
  due.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffMs = today.getTime() - due.getTime();
  return diffMs > 0 ? Math.floor(diffMs / (1000 * 60 * 60 * 24)) : 0;
}

function ThreadDetailModal({
  thread,
  open,
  onClose,
}: {
  thread: EmailThread | null;
  open: boolean;
  onClose: () => void;
}) {
  const archiveThread = useArchiveThread();
  const markRead = useMarkEmailRead();
  const starEmail = useStarEmail();

  if (!open || !thread) return null;

  const sortedMessages = [...(thread.messages || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const primaryMessage = sortedMessages[sortedMessages.length - 1];
  const senderLabel = primaryMessage?.fromName || primaryMessage?.fromAddress || 'Unknown sender';
  const recipientLabel =
    primaryMessage?.toAddresses?.map(item => item.name || item.email).join(', ') || 'No recipient';
  const threadTypeLabel = primaryMessage?.direction === 'INBOUND' ? 'Received email' : 'Sent email';

  return (
    <ModalContentWrapper
      open
      onClose={onClose}
      variant="right"
      title={thread.subject}
      classes={{
        title: 'font-serif text-[28px] leading-[34px]',
        dialogPanel: 'max-w-[700px] h-screen',
        children: 'px-8 pb-8 pt-5 h-full overflow-y-auto',
      }}
    >
      <div className="space-y-6">
        <div className="space-y-4 border-b border-gray-200 pb-6">
          <div className="text-sm text-gray-600">
            <span className="text-gray-500">Type:</span>{' '}
            <span className="font-medium text-gray-900">{threadTypeLabel}</span>
            <span className="ml-3 inline-flex align-middle">
              <ThreadCategoryBadge category={thread.category} />
            </span>
          </div>

          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-semibold text-gray-900">{senderLabel}</span>
              <span className="text-gray-500">&lt;{primaryMessage?.fromAddress}&gt;</span>
              <span className="text-gray-500">
                {primaryMessage ? new Date(primaryMessage.createdAt).toLocaleString('en-AU') : ''}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>to {recipientLabel}</span>
              <ChevronDown className="w-4 h-4" />
            </div>
            {thread.legalMatterRef && (
              <p className="text-xs font-medium text-blue-600 mt-2">{thread.legalMatterRef}</p>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => {
                archiveThread.mutate(thread.id, { onSuccess: onClose });
              }}
              disabled={archiveThread.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Archive className="w-4 h-4" />
              Archive
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {sortedMessages.map(message => (
            <div key={message.id} className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm font-semibold text-gray-900">
                  {message.fromName || message.fromAddress}
                </div>
                <div className="flex items-center gap-3">
                  {!message.isRead && message.direction === 'INBOUND' && (
                    <button
                      onClick={() => markRead.mutate(message.id)}
                      className="text-xs font-medium text-gray-500 hover:text-gray-900"
                    >
                      Mark as read
                    </button>
                  )}
                  <button
                    onClick={() =>
                      starEmail.mutate({
                        messageId: message.id,
                        starred: !message.isStarred,
                      })
                    }
                    className="text-gray-400 hover:text-gray-700"
                    title={message.isStarred ? 'Unstar' : 'Star'}
                  >
                    <Star
                      className={cn(
                        'w-4 h-4',
                        message.isStarred && 'text-yellow-500 fill-yellow-500',
                      )}
                    />
                  </button>
                </div>
              </div>

              <p className="mt-1 text-sm text-gray-500">
                to {message.toAddresses.map(item => item.name || item.email).join(', ')}
              </p>
              <p className="mt-5 text-base text-gray-900 whitespace-pre-wrap break-words leading-8">
                {message.bodyText}
              </p>
              <EmailAttachmentList attachments={message.attachments} />
              <div className="mt-6 flex items-center justify-between gap-3 text-xs text-gray-500">
                <span>{new Date(message.createdAt).toLocaleString('en-AU')}</span>
                <div className="flex items-center gap-3">
                  {message.openedAt && <span>Opened {fmtTimeAgo(message.openedAt)}</span>}
                  <span className="rounded bg-gray-100 px-2 py-1 font-medium text-gray-600">
                    {EMAIL_STATUS_CONFIG[message.status].label}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModalContentWrapper>
  );
}

export function EmailTab({ loanId, loan, borrowerEmail = '', borrowerName }: EmailTabProps) {
  const { data: threads, isLoading } = useEmailsByLoan(loanId);
  const sendEmail = useSendEmail();
  const loanNumber = loan.loanNumber;
  const user = useAuthStore(state => state.user);
  const activeTenantId = resolveActiveTenantId(user);
  const activeTenantName =
    resolveAvailableTenants(user).find(tenant => tenant.id === activeTenantId)?.name ||
    user?.availableTenants?.find(tenant => tenant.id === activeTenantId)?.name ||
    user?.availableTenants?.[0]?.name ||
    'Loan Manager';

  const [showComposeForm, setShowComposeForm] = useState(false);
  const [threadFilter, setThreadFilter] = useState<'all' | 'sent' | 'received' | 'starred'>('all');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [to, setTo] = useState(borrowerEmail);
  const [subject, setSubject] = useState(`Loan Reference: ${loanNumber}`);
  const [body, setBody] = useState('');
  const [bodyHtml, setBodyHtml] = useState<string | undefined>(undefined);
  const [selectedTemplateId, setSelectedTemplateId] = useState<EmailTemplateId | null>(null);
  const [showTemplateStatusWarning, setShowTemplateStatusWarning] = useState(false);

  const chronologicalMessages = (threads || [])
    .flatMap(thread =>
      (thread.messages || []).map(message => ({
        ...message,
        threadSubject: thread.subject,
      })),
    )
    .sort((a, b) => {
      const aTime = new Date(a.sentAt || a.receivedAt || a.createdAt).getTime();
      const bTime = new Date(b.sentAt || b.receivedAt || b.createdAt).getTime();
      return bTime - aTime;
    });
  const filteredThreads = (threads || [])
    .filter(thread => {
      if (threadFilter === 'sent')
        return thread.messages?.some(message => message.direction === 'OUTBOUND');
      if (threadFilter === 'received')
        return thread.messages?.some(message => message.direction === 'INBOUND');
      if (threadFilter === 'starred') return thread.messages?.some(message => message.isStarred);
      return true;
    })
    .sort((a, b) => {
      const aTime = new Date(a.lastMessageAt || a.createdAt).getTime();
      const bTime = new Date(b.lastMessageAt || b.createdAt).getTime();
      return bTime - aTime;
    });
  const selectedThread = (threads || []).find(thread => thread.id === selectedThreadId) || null;
  const selectedTemplateRule = selectedTemplateId
    ? EMAIL_TEMPLATE_RULES_BY_ID[selectedTemplateId]
    : null;
  const templateStatusMismatch =
    !!selectedTemplateRule && !selectedTemplateRule.matchingStatuses.includes(loan.status);
  const currentStatusLabel = LOAN_STATUS_CONFIG[loan.status]?.label ?? loan.status;
  const expectedStatusLabels =
    selectedTemplateRule?.matchingStatuses
      .map(status => LOAN_STATUS_CONFIG[status]?.label ?? status)
      .join(', ') ?? '';
  const templateStatusWarningDescription = selectedTemplateRule
    ? `${selectedTemplateRule.label} is intended for loans with status ${expectedStatusLabels}. This loan is currently ${currentStatusLabel}.`
    : '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading email history…
      </div>
    );
  }

  const resetComposeForm = () => {
    setShowComposeForm(false);
    setTo(borrowerEmail);
    setSubject(`Loan Reference: ${loanNumber}`);
    setBody('');
    setBodyHtml(undefined);
    setSelectedTemplateId(null);
    setShowTemplateStatusWarning(false);
  };

  const sendComposedEmail = async () => {
    try {
      await sendEmail.mutateAsync({
        to: to
          .split(',')
          .map(value => value.trim())
          .filter(Boolean),
        subject,
        body,
        bodyHtml,
        loanId,
      });
      resetComposeForm();
    } catch (error) {
      handleMutationError(error, 'Failed to send email');
    }
  };

  const handleSendEmail = async () => {
    if (templateStatusMismatch) {
      setShowTemplateStatusWarning(true);
      return;
    }

    await sendComposedEmail();
  };

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const renderLoanDetailsTable = (rows: Array<{ label: string; value: string }>) => {
    const validRows = rows.filter(row => row.value.trim() !== '');
    if (validRows.length === 0) return '';

    return `
      <table role="presentation" border="0" cellpadding="0" cellspacing="0" style="width: 100%; border-collapse: separate; border-spacing: 0; margin: 0 0 24px 0; border: 1px solid #D1D5DB; border-radius: 10px; overflow: hidden; background: #FFFFFF;">
        ${validRows
          .map(
            (row, index) => `
          <tr>
            <td style="padding: 14px 16px; width: 46%; border-bottom: ${index < validRows.length - 1 ? '1px solid #E5E7EB' : 'none'}; color: #6B7280; font-size: 13px; line-height: 20px; vertical-align: middle;">${escapeHtml(row.label)}</td>
            <td style="padding: 14px 16px; border-bottom: ${index < validRows.length - 1 ? '1px solid #E5E7EB' : 'none'}; color: #111827; font-size: 14px; line-height: 20px; font-weight: 500; vertical-align: middle;">${escapeHtml(row.value)}</td>
          </tr>`,
          )
          .join('')}
      </table>
    `;
  };

  const handleSelectTemplate = (template: EmailTemplateId) => {
    const schedule = [...(loan.schedule || [])].sort(
      (a: RepaymentScheduleItem, b: RepaymentScheduleItem) =>
        new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    );
    const nextScheduledPayment =
      schedule.find(item => item.status === 'PENDING' || item.status === 'PARTIAL') || schedule[0];
    const overduePayment =
      schedule.find(item => item.status === 'OVERDUE') ||
      schedule.find(
        item =>
          getDaysOverdue(item.dueDate) > 0 && item.status !== 'PAID' && item.status !== 'WAIVED',
      );

    const resolvedBorrowerName = borrowerName || loan.borrower?.name || 'Borrower';
    const resolvedLenderName = activeTenantName;
    const principal = fmtCurrency(loan.principal);
    const interestRate = `${Number(loan.interestRate || 0).toFixed(2)}%`;
    const termMonths = `${loan.termMonths}`;
    const settlementDate = fmtDateOnly(loan.settlementDate) || 'TBC';
    const maturityDate = fmtDateOnly(loan.maturityDate) || 'TBC';
    const amountDue = fmtCurrency(nextScheduledPayment?.totalDue || 0);
    const dueDate = fmtDateOnly(nextScheduledPayment?.dueDate) || 'TBC';
    const outstandingBalance = fmtCurrency(loan.principal);
    const payoutAmount = fmtCurrency(loan.principal);
    const arrearsAmount = fmtCurrency(
      overduePayment?.totalDue || nextScheduledPayment?.totalDue || 0,
    );
    const daysOverdue = `${getDaysOverdue(overduePayment?.dueDate)}`;
    const overdueDueDate =
      fmtDateOnly(overduePayment?.dueDate || nextScheduledPayment?.dueDate) || 'TBC';

    const templateContent: Record<
      EmailTemplateId,
      { subject: string; body: string; bodyHtml: string }
    > = {
      loan_approval: {
        subject: 'Approval Notice',
        body: `Dear ${resolvedBorrowerName},

Good news — your loan application has been approved.

  Loan reference:   ${loanNumber}
  Approved amount:  ${principal}
  Interest rate:    ${interestRate} p.a.
  Term:             ${termMonths} months

Next steps:
Please review and sign your loan documents so we can proceed to settlement.
Your settlement is currently scheduled for ${settlementDate}.

If you have any questions, reply to this email or contact your loan officer.

Kind regards,
${resolvedLenderName}`,
        bodyHtml: `
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Dear ${escapeHtml(resolvedBorrowerName)},</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Good news — your loan application has been approved.</p>
          ${renderLoanDetailsTable([
            { label: 'Loan reference', value: loanNumber },
            { label: 'Approved amount', value: principal },
            { label: 'Interest rate', value: `${interestRate} p.a.` },
            { label: 'Term', value: `${termMonths} months` },
          ])}
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;"><strong>Next steps:</strong><br />Please review and sign your loan documents so we can proceed to settlement.<br />Your settlement is currently scheduled for ${escapeHtml(settlementDate)}.</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">If you have any questions, reply to this email or contact your loan officer.</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0;">Kind regards,<br />${escapeHtml(resolvedLenderName)}</p>
        `,
      },
      payment_reminder: {
        subject: 'Payment Reminder',
        body: `Dear ${resolvedBorrowerName},

This is a friendly reminder that your next loan repayment is coming up.

  Loan reference:   ${loanNumber}
  Amount due:       ${amountDue}
  Due date:         ${dueDate}
  Outstanding balance: ${outstandingBalance}

Please arrange to make this payment yourself on or before the due date to avoid any late fees or arrears.

If you have already made this payment, please disregard this message.

Kind regards,
${resolvedLenderName}`,
        bodyHtml: `
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Dear ${escapeHtml(resolvedBorrowerName)},</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">This is a friendly reminder that your next loan repayment is coming up.</p>
          ${renderLoanDetailsTable([
            { label: 'Loan reference', value: loanNumber },
            { label: 'Amount due', value: amountDue },
            { label: 'Due date', value: dueDate },
            { label: 'Outstanding balance', value: outstandingBalance },
          ])}
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Please arrange to make this payment yourself on or before the due date to avoid any late fees or arrears.</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">If you have already made this payment, please disregard this message.</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0;">Kind regards,<br />${escapeHtml(resolvedLenderName)}</p>
        `,
      },
      maturity_warning: {
        subject: 'Maturity Warning',
        body: `Dear ${resolvedBorrowerName},

Your loan is approaching its maturity date and will need to be finalised.

  Loan reference:     ${loanNumber}
  Maturity date:      ${maturityDate}
  Payout amount:      ${payoutAmount}

To finalise your loan, please arrange for the full payout amount to be paid on or before the maturity date. If you would like to discuss refinancing or an extension, please contact us as soon as possible.

Kind regards,
${resolvedLenderName}`,
        bodyHtml: `
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Dear ${escapeHtml(resolvedBorrowerName)},</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Your loan is approaching its maturity date and will need to be finalised.</p>
          ${renderLoanDetailsTable([
            { label: 'Loan reference', value: loanNumber },
            { label: 'Maturity date', value: maturityDate },
            { label: 'Payout amount', value: payoutAmount },
          ])}
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">To finalise your loan, please arrange for the full payout amount to be paid on or before the maturity date. If you would like to discuss refinancing or an extension, please contact us as soon as possible.</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0;">Kind regards,<br />${escapeHtml(resolvedLenderName)}</p>
        `,
      },
      arrears_notice: {
        subject: 'Arrears Notice',
        body: `Dear ${resolvedBorrowerName},

Our records show that a payment on your loan is now overdue.

  Loan reference:     ${loanNumber}
  Amount overdue:     ${arrearsAmount}
  Days overdue:       ${daysOverdue}
  Original due date:  ${overdueDueDate}

Please make this payment as soon as possible to bring your account up to date and avoid further late fees. If you have any questions about your account, please contact us.

Kind regards,
${resolvedLenderName}`,
        bodyHtml: `
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Dear ${escapeHtml(resolvedBorrowerName)},</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Our records show that a payment on your loan is now overdue.</p>
          ${renderLoanDetailsTable([
            { label: 'Loan reference', value: loanNumber },
            { label: 'Amount overdue', value: arrearsAmount },
            { label: 'Days overdue', value: daysOverdue },
            { label: 'Original due date', value: overdueDueDate },
          ])}
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0 0 20px 0;">Please make this payment as soon as possible to bring your account up to date and avoid further late fees. If you have any questions about your account, please contact us.</p>
          <p style="font-size: 14px; line-height: 22px; color: #111827; margin: 0;">Kind regards,<br />${escapeHtml(resolvedLenderName)}</p>
        `,
      },
    };

    const selected = templateContent[template];
    if (!selected) return;

    setTo(borrowerEmail);
    setSubject(selected.subject);
    setBody(selected.body);
    setBodyHtml(selected.bodyHtml);
    setSelectedTemplateId(template);
    setShowTemplateStatusWarning(false);
    setShowComposeForm(true);
  };

  const templates = EMAIL_TEMPLATE_RULES;

  const stats = chronologicalMessages.reduce(
    (acc: { totalSent: number; delivered: number; failed: number }, email) => {
      if (email.direction === 'OUTBOUND') {
        acc.totalSent += 1;
      }
      if (email.status === 'SENT' || email.status === 'DELIVERED') {
        acc.delivered += 1;
      }
      if (email.status === 'FAILED' || email.status === 'BOUNCED') {
        acc.failed += 1;
      }
      return acc;
    },
    { totalSent: 0, delivered: 0, failed: 0 },
  );

  return (
    <div className="space-y-6">
      {/* Quick Send Templates */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Quick Send Templates</h3>
        <div className="grid grid-cols-2 gap-3">
          {templates.map(tpl => (
            <button
              key={tpl.id}
              onClick={() => handleSelectTemplate(tpl.id)}
              disabled={!borrowerEmail}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {tpl.label}
            </button>
          ))}
        </div>
      </div>

      {/* Email Compose */}
      {showComposeForm ? (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Send Email</h3>
          <div className="space-y-4">
            {templateStatusMismatch && (
              <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p>{templateStatusWarningDescription}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
              <input
                type="text"
                value={to}
                onChange={e => setTo(e.target.value)}
                placeholder="borrower@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                placeholder="Type your message here…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                rows={6}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                onClick={handleSendEmail}
                disabled={sendEmail.isPending || !to || !subject || !body}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                {sendEmail.isPending ? 'Sending…' : 'Send Email'}
              </Button>
              <button
                onClick={resetComposeForm}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-5 text-center">
          <Button
            variant="primary"
            onClick={() => setShowComposeForm(true)}
            className="px-4 py-2 text-white rounded-md text-sm font-medium hover: transition-colors"
          >
            Compose Email
          </Button>
        </div>
      )}

      {/* Email Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Total Sent</p>
          <p className="text-2xl font-semibold text-gray-900">{stats.totalSent}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Delivered</p>
          <p className="text-2xl font-semibold text-green-600">{stats.delivered}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Failed</p>
          <p className="text-2xl font-semibold text-red-600">{stats.failed}</p>
        </div>
      </div>

      {/* Correspondence */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-gray-400" />
            <h3 className="font-semibold text-gray-900">Correspondence</h3>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {(['all', 'sent', 'received', 'starred'] as const).map(filter => (
              <button
                key={filter}
                onClick={() => setThreadFilter(filter)}
                className={cn(
                  'inline-flex min-w-[110px] shrink-0 items-center justify-center rounded-md border px-4 py-2 text-sm font-medium transition-colors',
                  threadFilter === filter
                    ? 'border-gray-800 bg-gray-700 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                )}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {filteredThreads.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">No correspondence yet</p>
        ) : (
          <div className="divide-y divide-gray-200 rounded-lg border border-gray-200 overflow-hidden bg-white">
            {filteredThreads.map(thread => {
              const lastMessage = [...(thread.messages || [])].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
              )[0];
              const threadMessageCount = thread.messageCount || thread.messages?.length || 0;
              const hasStarred = thread.messages?.some(message => message.isStarred);
              const displayParticipants = getDisplayParticipants(thread.participants);
              return (
                <button
                  key={thread.id}
                  onClick={() => setSelectedThreadId(thread.id)}
                  className="w-full text-left px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{thread.subject}</p>
                        <ThreadCategoryBadge category={thread.category} />
                        {threadMessageCount > 1 && (
                          <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                            {threadMessageCount}
                          </span>
                        )}
                        {hasStarred && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate mt-1">
                        {displayParticipants
                          .map(participant => participant.name || participant.email)
                          .join(', ')}
                      </p>
                      {lastMessage && (
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {trimQuotedReplyContent(lastMessage.bodyText || lastMessage.subject)}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-500">
                        {thread.lastMessageAt ? fmtTimeAgo(thread.lastMessageAt) : 'No messages'}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {lastMessage?.direction === 'INBOUND' ? 'Received' : 'Sent'}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <ThreadDetailModal
        thread={selectedThread}
        open={!!selectedThread}
        onClose={() => setSelectedThreadId(null)}
      />
      <ConfirmDialog
        open={showTemplateStatusWarning}
        onClose={() => setShowTemplateStatusWarning(false)}
        onConfirm={() => {
          setShowTemplateStatusWarning(false);
          void sendComposedEmail();
        }}
        title="Template status mismatch"
        description={`${templateStatusWarningDescription} Send this email anyway?`}
        confirmLabel="Send Anyway"
        cancelLabel="Review Email"
        variant="warning"
        loading={sendEmail.isPending}
      />
    </div>
  );
}
